/* iceberg-ui */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { Logo } from 'application/Shared/Components/App';
import { getClientName } from 'application/Shared/Helpers/tenant';

/* Dates */
import { format } from 'date-fns';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const formatDate = (timestamp: number) =>
    format(new Date(timestamp), 'MMMM d, yyyy');

const ReportMetaEntry = ({
    label,
    value
}: {
    label: string;
    value: string;
}) => (
    <Flex sx={{ alignItems: 'center', padding: '4px 0' }}>
        <Typography sx={{ fontWeight: 600, minWidth: '120px' }} variant="body1">
            {label}:
        </Typography>
        <Typography variant="body1">{value}</Typography>
    </Flex>
);

const ReportMeta = ({
    startDate,
    endDate,
    platformTenantId
}: {
    startDate: number;
    endDate: number;
    platformTenantId?: string;
}) => {
    const { t } = useTranslation();
    const { partnerId, clientId } = useParams();

    const session = useSession();
    const clientName = getClientName({
        clientId: clientId || platformTenantId || partnerId,
        session
    });

    return (
        <Box>
            <ReportMetaEntry
                label={t('Report.Header.createdOn')}
                value={formatDate(new Date().getTime())}
            />
            <ReportMetaEntry
                label={t('Report.Header.period')}
                value={`${formatDate(startDate)} - ${formatDate(endDate)}`}
            />
            {clientName && (
                <ReportMetaEntry
                    label={t('Report.Header.preparedFor')}
                    value={clientName}
                />
            )}
        </Box>
    );
};

const LogoAndTitle = ({ reportType }: { reportType: string }) => {
    const { t } = useTranslation();

    return (
        <Flex>
            <Logo
                checkTenantSettings
                lightColor={false}
                style={{
                    height: '50px',
                    marginBottom: '0',
                    marginRight: '24px',
                    width: 'auto'
                }}
                containerStyle={{
                    marginBottom: '0',
                    width: 'auto'
                }}
            />
            <Typography variant="h1">
                {t(`Reports.Title.${reportType}.report`)}
            </Typography>
        </Flex>
    );
};

export const ReportHeader = ({
    startDate,
    endDate,
    platformTenantId,
    reportType
}: {
    startDate: number;
    endDate: number;
    platformTenantId?: string;
    reportType: string;
}) => {
    const theme = useTheme();
    return (
        <Card
            data-testid="reportHeader"
            sx={{
                border: '1px solid',
                borderColor: theme.palette.border.pageHeader,
                boxShadow: theme.palette.reports.v2Header.boxShadow,
                marginBottom: '48px',
                padding: '24px 32px'
            }}
        >
            <Flex
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
                <LogoAndTitle reportType={reportType} />
                <ReportMeta
                    startDate={startDate}
                    endDate={endDate}
                    platformTenantId={platformTenantId}
                />
            </Flex>
        </Card>
    );
};
